"use client";

import { useActionState, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useFormStatus } from "react-dom";
import { useRouter } from "@frontend/lib/navigation";
import { counterOfferAction, type ActionState } from "@frontend/lib/actions";
import { PaymentPlanPicker } from "@frontend/components/payment-plan-picker";
import type { CollabRoom } from "@creators/backend/lib/collabs/queries";
import {
  DEFAULT_PRESET,
  SCHEDULE_PRESETS,
  type SchedulePresetKey,
} from "@creators/backend/lib/collabs/schedule";
import { paiseToRupeeString, parseRupeesToPaise } from "@creators/backend/lib/money";

export function CounterOfferDialog({ room }: { room: CollabRoom }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full rounded-lg border border-line bg-surface px-3 py-1.5 text-sm font-medium text-ink transition hover:bg-canvas"
      >
        Counter offer
      </button>

      {open ? (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 p-4 sm:items-center"
          onClick={() => setOpen(false)}
        >
          <div
            className="max-h-[min(640px,90dvh)] w-full max-w-md overflow-y-auto rounded-2xl bg-surface shadow-xl"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="counter-offer-title"
          >
            <CounterOfferForm room={room} onClose={() => setOpen(false)} />
          </div>
        </div>
      ) : null}
    </>
  );
}

function CounterOfferForm({ room, onClose }: { room: CollabRoom; onClose: () => void }) {
  const router = useRouter();
  const offer = room.currentOffer!;

  // Which preset the offer on the table matches, so the picker opens on the terms
  // being countered rather than on a default the other side never proposed.
  const presetKey =
    (Object.keys(SCHEDULE_PRESETS) as SchedulePresetKey[]).find(
      (key) => JSON.stringify(SCHEDULE_PRESETS[key].schedule) === JSON.stringify(offer.schedule),
    ) ?? DEFAULT_PRESET;

  const [state, formAction] = useActionState<ActionState, FormData>(counterOfferAction, {});
  const [amount, setAmount] = useState(
    offer.amountTotal > 0 ? paiseToRupeeString(offer.amountTotal) : "",
  );
  const [plan, setPlan] = useState<SchedulePresetKey>(presetKey);
  const handled = useRef<ActionState | null>(null);

  const amountPaise = useMemo(() => {
    const parsed = parseRupeesToPaise(amount);
    return parsed && parsed > 0 ? parsed : null;
  }, [amount]);

  useEffect(() => {
    if (state === handled.current) return;
    handled.current = state;
    if (state.ok) {
      onClose();
      router.refresh();
    }
  }, [state, onClose, router]);

  return (
    <form action={formAction} className="p-5">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h3 id="counter-offer-title" className="font-semibold text-ink">
            Counter offer
          </h3>
          <p className="mt-0.5 text-sm text-ink-muted">Propose a new amount, or a new split.</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg px-2 py-1 text-sm text-ink-muted hover:bg-canvas hover:text-ink"
          aria-label="Close"
        >
          ✕
        </button>
      </div>

      <input type="hidden" name="collabId" value={room.collab.id} />
      <input type="hidden" name="deliverBy" value={offer.deliverBy} />
      <input type="hidden" name="brief" value={offer.brief} />
      <input type="hidden" name="revisionsIncluded" value={offer.revisionsIncluded} />
      <input type="hidden" name="livePeriodDays" value={offer.livePeriodDays} />

      <div className="space-y-4">
        <CompactField label="Your counter amount">
          <div className="relative">
            <span className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-ink-muted">
              ₹
            </span>
            <input
              name="amount"
              inputMode="numeric"
              required
              autoFocus
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              className="field-compact tabular pl-7"
            />
          </div>
        </CompactField>

        <PaymentPlanPicker
          name="preset"
          value={plan}
          onChange={setPlan}
          amountPaise={amountPaise}
        />

        <p className="text-xs leading-relaxed text-ink-muted">
          The delivery date and brief stay as they are. Anything else, say it in chat.
        </p>
      </div>

      {state.error ? <p className="mt-3 text-sm text-warn-700">{state.error}</p> : null}

      <div className="mt-5 flex gap-2">
        <SendButton />
        <button
          type="button"
          onClick={onClose}
          className="shrink-0 rounded-xl px-4 py-2.5 text-sm font-medium text-ink-muted hover:bg-canvas"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

function CompactField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-ink-muted">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function SendButton() {
  const { pending } = useFormStatus();
  return (
    <button
      type="submit"
      disabled={pending}
      className="inline-flex flex-1 items-center justify-center rounded-xl bg-brand-600 px-4 py-2.5 text-sm font-medium text-canvas transition hover:bg-brand-700 disabled:opacity-60"
    >
      {pending ? "Sending…" : "Send counter"}
    </button>
  );
}
